import { Component, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { FormControlNames } from '../../shared/enums';
import { ResetPassword } from '../../shared/models/rest-password.model';
import { PasswordErrorService } from '../../shared/services/password-error.service';
import { PasswordRecoveryService } from '../password-recovery/services/password-recovery.service';

@Component({
  selector: 'app-password-reset',
  templateUrl: './password-reset.component.html',
  styleUrls: ['./password-reset.component.scss'],
  standalone: false,
})
export class PasswordResetComponent implements OnInit {
  resetEnabled = false;
  username!: string;
  token!: string;
  resetPassword: ResetPassword = {} as ResetPassword;
  passwordForm!: FormGroup;
  passwordControl!: FormControl;

  constructor(
    private passwordErrorService: PasswordErrorService,
    private router: Router,
    private route: ActivatedRoute,
    private passRecoveryService: PasswordRecoveryService,
    private translate: TranslateService,
    private snackBar: MatSnackBar,
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      this.username = params['username'];
      this.token = params['passwordResetToken'];
    });
  }

  private initForm(): void {
    this.passwordControl = this.passwordErrorService.createFormControl();
    const confirmPasswordControl = new FormControl('', [Validators.required]);
    this.passwordForm = new FormGroup(
      {
        [FormControlNames.PASSWORD]: this.passwordControl,
        [FormControlNames.CONFIRM_PASSWORD]: confirmPasswordControl,
      },
      {
        validators: this.passwordErrorService.createPasswordMatchValidator(FormControlNames.PASSWORD, FormControlNames.CONFIRM_PASSWORD),
      },
    );

    this.passwordControl.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => {
      this.resetPassword.password1 = value;
    });

    confirmPasswordControl.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => {
      if (value != null) {
        this.resetPassword.password2 = value;
      }
    });
  }

  getPasswordErr(): void {
    return this.passwordErrorService.getPasswordErr(this.passwordControl);
  }

  getConfirmPwdErr(): void {
    return this.passwordErrorService.getConfirmPwdErr(this.passwordForm.controls[FormControlNames.CONFIRM_PASSWORD]);
  }

  onFocus(): void {
    this.passwordForm.get(FormControlNames.CONFIRM_PASSWORD)?.markAsTouched();
  }

  reset(): void {
    this.passRecoveryService.resetPassword(this.username, this.token, this.resetPassword).subscribe(
      () => {
        this.router.navigate(['/']);
        setTimeout(() => {
          this.translate.get('PASS_RECOVERY_SUCCESS').subscribe((res: string) => {
            this.snackBar.open(res, 'Close', {
              duration: 10000,
              panelClass: ['snackbar-success'],
            });
          });
        }, 800);
      },
      (error) => {
        const errorMessage = error?.error?.message ?? 'PASS_RESET_CHANGED_ERROR';

        this.translate.get(errorMessage).subscribe((res: string) => {
          this.snackBar.open(res, 'Close', {
            duration: 8000,
            panelClass: ['snackbar-error'],
          });
        });
      },
    );
  }
}
